import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import { GetApp, AttachFile, ArrowBack, TableChart, Upload, CloudDownload, DeleteOutline } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import {
  canEditPurchaseOrder,
  canApproveStorePurchaseOrder,
  canApproveAmPurchaseOrder,
  canSuperAdminDeletePurchaseOrder
} from '../../utils/purchaseOrderPermissions';
import { formatDateWithTimezone } from '../../utils/timezoneUtils';
import StoreDocumentsDialog from './StoreDocumentsDialog';
import PurchaseOrderDeleteDialog from './PurchaseOrderDeleteDialog';
import {
  PO_LINE_CLOSURE_REASON_LABELS,
  MIN_PO_LINE_CLOSURE_REMARKS_LENGTH,
  canClosePoLine
} from '../../constants/purchaseOrderLineClosure';
import { downloadCsvFile } from '../../utils/csvHelpers';
import { unitMetricLabel } from '../../constants/unitMetrics';
import { inventoryCategoryLabel, normalizeInventoryCategory } from '../../constants/inventoryCategories';

const PO_LINE_ITEMS_SIMPLE_CSV_HEADERS = ['Part Number', 'Description', 'Category', 'Qty'];
const PO_DOCUMENT_MAX_SIZE_MB = 25;

/** DN / AWB / BOE editable until PO is closed or cancelled */
const PO_SHIPPING_REF_EDITABLE_STATUSES = [
  'DRAFT',
  'PENDING_STORE_APPROVAL',
  'PENDING_AM_APPROVAL',
  'ISSUED',
  'PARTIALLY_RECEIVED',
  'RECEIVED'
];

const SHIPPING_DOC_FIELDS = [
  { key: 'dnNo', label: 'DN No', docCategory: 'PO_DN' },
  { key: 'awbNo', label: 'AWB No', docCategory: 'PO_AWB' },
  { key: 'boeNo', label: 'BOE No', docCategory: 'PO_BOE' },
  { key: 'invoiceNo', label: 'Invoice No', docCategory: 'PO_INVOICE' }
];


const statusColor = (status) => {
  const map = {
    DRAFT: 'default',
    PENDING_STORE_APPROVAL: 'warning',
    PENDING_AM_APPROVAL: 'warning',
    ISSUED: 'info',
    PARTIALLY_RECEIVED: 'primary',
    RECEIVED: 'success',
    CLOSED: 'default',
    CANCELLED: 'error'
  };
  return map[status] || 'default';
};

const PurchaseOrderDetail = ({ poId, onBack, onEdit, onDeleted, displayTimezone }) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [po, setPo] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, remarks: '', action: null });
  const [docDialog, setDocDialog] = useState(false);
  const [shippingRefs, setShippingRefs] = useState({ dnNo: '', awbNo: '', boeNo: '', invoiceNo: '' });
  const [shippingSaveLoading, setShippingSaveLoading] = useState(false);
  const [shippingDocs, setShippingDocs] = useState([]);
  const [shippingDocLoadingByCategory, setShippingDocLoadingByCategory] = useState({});
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [fieldDocsDialog, setFieldDocsDialog] = useState({ open: false, category: '', label: '' });
  const [closeLineDialog, setCloseLineDialog] = useState({
    open: false,
    lineId: '',
    partNumber: '',
    closureRemarks: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const canManageStorePo = [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
  const isSuperAdmin = canSuperAdminDeletePurchaseOrder(user?.role);
  const canCancel = [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.SUPER_ADMIN].includes(user?.role);
  const canEditPo = po && canEditPurchaseOrder(user?.role, po.status);
  const canApproveStore = po && canApproveStorePurchaseOrder(user?.role, po.status);
  const canApproveAm = po && canApproveAmPurchaseOrder(user?.role, po.status, po, user);

  const fetchPo = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/store/purchase-orders/${poId}`);
      setPo(res.data);
      setSnapshot(null);
      setSelectedVersion(null);
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to load purchase order.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [poId, showToast]);

  useEffect(() => {
    fetchPo();
  }, [fetchPo]);

  useEffect(() => {
    if (!po) return;
    setShippingRefs({
      dnNo: po.dnNo || '',
      awbNo: po.awbNo || '',
      boeNo: po.boeNo || '',
      invoiceNo: po.invoiceNo || ''
    });
  }, [po]);

  const fetchShippingDocs = useCallback(async () => {
    if (!poId) return;
    try {
      const res = await axios.get(`/api/store/purchase-orders/${poId}/documents`);
      setShippingDocs(res.data || []);
    } catch {
      setShippingDocs([]);
    }
  }, [poId]);

  useEffect(() => {
    fetchShippingDocs();
  }, [fetchShippingDocs]);

  useEffect(() => {
    if (!poId || !po) return;
    const load = async () => {
      try {
        const res = await axios.get(`/api/store/purchase-orders/${poId}/versions`);
        setVersions(res.data || []);
      } catch (e) {
        console.error('Failed to load versions', e);
      }
    };
    load();
  }, [poId, po]);

  useEffect(() => {
    if (!poId || selectedVersion == null) return;
    if (selectedVersion === 'current' || selectedVersion === '') {
      setSnapshot(null);
      return;
    }
    const load = async () => {
      try {
        const res = await axios.get(`/api/store/purchase-orders/${poId}/versions/${selectedVersion}`);
        setSnapshot(res.data);
      } catch (e) {
        showToast(getErrorMessage(e, 'Failed to load version snapshot.'), 'error');
      }
    };
    load();
  }, [poId, selectedVersion, showToast]);

  const displayData = snapshot || po;
  const isVersionView = Boolean(snapshot);
  const shippingRefsEditable =
    !isVersionView &&
    canManageStorePo &&
    po &&
    PO_SHIPPING_REF_EDITABLE_STATUSES.includes(po.status);

  const runAction = async (action, body = {}) => {
    setActionLoading(true);
    try {
      await axios.post(`/api/store/purchase-orders/${poId}/${action}`, body);
      showToast('Action completed', 'success');
      setRejectDialog({ open: false, remarks: '', action: null });
      fetchPo();
    } catch (e) {
      showToast(getErrorMessage(e, 'Action failed. Please try again.'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    const action = rejectDialog.action;
    if (!action) return;
    runAction(action, { remarks: rejectDialog.remarks });
  };

  const openCloseLineDialog = (line) => {
    setCloseLineDialog({
      open: true,
      lineId: line._id,
      partNumber: line.partNumber,
      closureRemarks: ''
    });
  };

  const handleCloseLine = async () => {
    const remarks = String(closeLineDialog.closureRemarks || '').trim();
    if (remarks.length < MIN_PO_LINE_CLOSURE_REMARKS_LENGTH) {
      showToast(`Closure remarks are required (minimum ${MIN_PO_LINE_CLOSURE_REMARKS_LENGTH} characters).`, 'error');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`/api/store/purchase-orders/${poId}/line-items/${closeLineDialog.lineId}/close`, {
        closureReason: 'PART_CANCELLED',
        closureRemarks: remarks
      });
      showToast('PO line closed', 'success');
      setCloseLineDialog({ open: false, lineId: '', partNumber: '', closureRemarks: '' });
      fetchPo();
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to close PO line.'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const renderLineStatus = (line) => {
    if (line.lineStatus === 'CLOSED') {
      const label = PO_LINE_CLOSURE_REASON_LABELS[line.closureReason] || 'Closed';
      const detail = [
        line.closureRemarks,
        line.closedAt ? `Closed ${new Date(line.closedAt).toLocaleString()}` : '',
        line.closedBy?.firstName ? `by ${line.closedBy.firstName}` : ''
      ].filter(Boolean).join(' · ');
      return (
        <Tooltip title={detail || label}>
          <Chip label={label} size="small" color="warning" variant="outlined" />
        </Tooltip>
      );
    }
    const received = Number(line.receivedQuantity) || 0;
    const ordered = Number(line.quantity) || 0;
    if (received > 0 && received < ordered) {
      return <Chip label="Partial" size="small" color="primary" variant="outlined" />;
    }
    return <Chip label="Open" size="small" variant="outlined" />;
  };

  const handleExportCsv = async () => {
    try {
      const res = await axios.get(`/api/store/purchase-orders/${poId}/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `PO-${po?.poNumber || poId}.csv`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('CSV exported', 'success');
    } catch (e) {
      showToast(getErrorMessage(e, 'Export failed.'), 'error');
    }
  };

  const handleDownloadLineItemsCsv = () => {
    const rows = (displayData.lineItems || [])
      .map((line) => ({
        'Part Number': String(line.partNumber || '').trim(),
        Description: String(line.description || '').trim(),
        Category: normalizeInventoryCategory(line.category),
        Qty: line.quantity ?? ''
      }))
      .filter((row) => row['Part Number']);
    if (!rows.length) {
      showToast('No line items to download', 'warning');
      return;
    }
    downloadCsvFile(
      `PO-${displayData.poNumber || poId}-line-items.csv`,
      PO_LINE_ITEMS_SIMPLE_CSV_HEADERS,
      rows
    );
    showToast('Line items downloaded', 'success');
  };

  const handleSaveShippingRefs = async () => {
    if (!poId) return;
    setShippingSaveLoading(true);
    try {
      const res = await axios.patch(`/api/store/purchase-orders/${poId}/shipping-references`, {
        dnNo: shippingRefs.dnNo,
        awbNo: shippingRefs.awbNo,
        boeNo: shippingRefs.boeNo,
        invoiceNo: shippingRefs.invoiceNo
      });
      setPo(res.data);
      showToast('DN / AWB / BOE / Invoice saved', 'success');
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to save shipping references.'), 'error');
    } finally {
      setShippingSaveLoading(false);
    }
  };

  const getLatestDocByCategory = (category) =>
    (shippingDocs || [])
      .filter((doc) => doc.category === category)
      .sort((a, b) => new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime())[0];
  const getDocsByCategory = (category) =>
    (shippingDocs || [])
      .filter((doc) => doc.category === category)
      .sort((a, b) => new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime());

  const setDocBusy = (category, busy) => {
    setShippingDocLoadingByCategory((prev) => ({ ...prev, [category]: busy }));
  };

  const handleUploadShippingDoc = async (category, file) => {
    if (!file || !poId) return;

    if (file.size > PO_DOCUMENT_MAX_SIZE_MB * 1024 * 1024) {
      showToast(`Document is too large. Maximum allowed size is ${PO_DOCUMENT_MAX_SIZE_MB}MB.`, 'error');
      return;
    }

    setDocBusy(category, true);
    try {
      const formData = new FormData();
      formData.append('documents', file);
      formData.append('category', category);
      await axios.post(`/api/store/purchase-orders/${poId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Document uploaded', 'success');
      fetchShippingDocs();
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to upload document.'), 'error');
    } finally {
      setDocBusy(category, false);
    }
  };

  const handleDeleteShippingDoc = async (docId, category) => {
    if (!docId || !poId) return;
    setDocBusy(category, true);
    try {
      await axios.delete(`/api/store/purchase-orders/${poId}/documents/${docId}`);
      showToast('Document deleted', 'success');
      setShippingDocs((prev) => (prev || []).filter((doc) => doc._id !== docId));
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to delete document.'), 'error');
    } finally {
      setDocBusy(category, false);
    }
  };

  const handleDownloadShippingDoc = async (doc) => {
    if (!doc?._id || !poId || downloadingDocId) return;
    setDownloadingDocId(doc._id);
    try {
      const response = await axios.get(
        `/api/store/purchase-orders/${poId}/documents/${doc._id}/download`,
        { responseType: 'blob' }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.originalName || doc.filename || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to download document.'), 'error');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`/api/store/purchase-orders/${poId}/export/xlsx`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `PO-${po?.poNumber || poId}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Excel exported', 'success');
    } catch (e) {
      showToast(getErrorMessage(e, 'Excel export failed.'), 'error');
    }
  };


  if (loading || !po) {
    return (
      <Box>
        {onBack && <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>Back</Button>}
        <Typography color="text.secondary">{loading ? 'Loading…' : 'Purchase order not found.'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && <Button startIcon={<ArrowBack />} onClick={onBack}>Back</Button>}
          <Typography variant="h6">{displayData.poNumber}</Typography>
          <Chip label={displayData.status} color={statusColor(displayData.status)} size="small" />
          {isVersionView && <Chip label={`Version ${selectedVersion}`} variant="outlined" size="small" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!isVersionView && canEditPo && onEdit && (
            <Button variant="outlined" size="small" onClick={() => onEdit(poId)}>
              {po.status === 'PENDING_AM_APPROVAL' ? 'Edit before approval' : 'Edit'}
            </Button>
          )}
          {!isVersionView && (
            <>
              <Button variant="outlined" size="small" startIcon={<AttachFile />} onClick={() => setDocDialog(true)}>
                Attachments
              </Button>
              <Button variant="outlined" size="small" startIcon={<GetApp />} onClick={handleExportCsv}>
                Export CSV
              </Button>
              <Button variant="outlined" size="small" startIcon={<TableChart />} onClick={handleExportExcel}>
                Export Excel
              </Button>
              {isSuperAdmin && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteOutline />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete PO
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {!isVersionView && versions.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>View version</InputLabel>
            <Select
              value={selectedVersion === null ? 'current' : selectedVersion}
              label="View version"
              onChange={(e) => setSelectedVersion(e.target.value === 'current' ? null : e.target.value)}
            >
              <MenuItem value="current">Current</MenuItem>
              {versions.map((v) => (
                <MenuItem key={v.version} value={v.version}>
                  Version {v.version} {v.createdAt ? new Date(v.createdAt).toLocaleString() : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Header</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Order To</Typography>
            <Typography variant="body2">{displayData.orderTo?.name || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Bill To</Typography>
            <Typography variant="body2">{displayData.billTo?.name || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Ship To</Typography>
            <Typography variant="body2">{displayData.shipTo?.name || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Operator</Typography>
            <Typography variant="body2">
              {displayData.operatorId?.name
                ? `${displayData.operatorId.name}${displayData.operatorId.code ? ` (${displayData.operatorId.code})` : ''}`
                : (displayData.operatorName || displayData.operatorCode || '—')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Aircraft</Typography>
            <Typography variant="body2">
              {displayData.aircraftRegistration || displayData.aircraftId?.registration || '—'}
              {displayData.aircraftModelVariant || displayData.aircraftId?.model ? ` (${displayData.aircraftModelVariant || displayData.aircraftId?.model})` : ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Expected Delivery</Typography>
            <Typography variant="body2">
              {displayData.expectedDeliveryDate ? (displayTimezone ? formatDateWithTimezone(displayData.expectedDeliveryDate, displayTimezone) : new Date(displayData.expectedDeliveryDate).toLocaleDateString()) : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Priority</Typography>
            <Typography variant="body2">
              {({ CRITICAL: 'AOG (Aircraft On Ground) – Critical', HIGH: 'Rush (Urgent) – High', MEDIUM: 'Normal (Regular/Routine) – Medium' }[displayData.priority] || '—')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Issued At</Typography>
            <Typography variant="body2">
              {displayData.issuedAt ? new Date(displayData.issuedAt).toLocaleString() : '—'}
            </Typography>
          </Grid>
          {displayData.notes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{displayData.notes}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>


      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Shipping / customs references
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          DN No, AWB No, BOE No, and Invoice No can be entered or updated while the order is open (including after issue), until the PO is closed or cancelled.
        </Typography>
        <Grid container spacing={2}>
          {SHIPPING_DOC_FIELDS.map((field) => {
            const doc = getLatestDocByCategory(field.docCategory);
            const docBusy = Boolean(shippingDocLoadingByCategory[field.docCategory]);
            return (
              <Grid item xs={12} sm={6} md={3} key={field.key}>
                {shippingRefsEditable ? (
                  <TextField
                    fullWidth
                    size="small"
                    label={field.label}
                    value={shippingRefs[field.key] || ''}
                    onChange={(e) => setShippingRefs((s) => ({ ...s, [field.key]: e.target.value }))}
                  />
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                    <Typography variant="body2">{displayData[field.key] || '—'}</Typography>
                  </>
                )}
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    startIcon={<Upload fontSize="small" />}
                    disabled={docBusy}
                  >
                    Attach
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadShippingDoc(field.docCategory, file);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  <Tooltip title={doc ? `Download ${doc.originalName || doc.filename}` : 'No document uploaded'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadShippingDoc(doc)}
                        disabled={!doc || docBusy || downloadingDocId === doc?._id}
                      >
                        <CloudDownload fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={doc ? 'Delete document' : 'No document uploaded'}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteShippingDoc(doc._id, field.docCategory)}
                        disabled={!doc || docBusy}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setFieldDocsDialog({ open: true, category: field.docCategory, label: field.label })}
                  >
                    View all
                  </Button>
                </Box>
                {doc && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }} noWrap>
                    {doc.originalName || doc.filename}
                  </Typography>
                )}
              </Grid>
            );
          })}
          {shippingRefsEditable && (
            <Grid item xs={12}>
              <Button variant="outlined" size="small" onClick={handleSaveShippingRefs} disabled={shippingSaveLoading}>
                {shippingSaveLoading ? 'Saving…' : 'Save DN / AWB / BOE / Invoice'}
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">Line Items</Typography>
          <Button variant="outlined" size="small" startIcon={<GetApp />} onClick={handleDownloadLineItemsCsv}>
            Download
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Part Number</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Received</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Remarks</TableCell>
                {!isVersionView && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(displayData.lineItems || []).map((line) => (
                <TableRow key={line._id || line.lineNumber}>
                  <TableCell>{line.lineNumber}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{line.partNumber}</TableCell>
                  <TableCell>{line.description || '—'}</TableCell>
                  <TableCell>{inventoryCategoryLabel(line.category)}</TableCell>
                  <TableCell>{line.quantity}</TableCell>
                  <TableCell>{line.receivedQuantity ?? 0}</TableCell>
                  <TableCell>{renderLineStatus(line)}</TableCell>
                  <TableCell>{unitMetricLabel(line.unit || 'EACH')}</TableCell>
                  <TableCell>
                    {line.lineStatus === 'CLOSED' && line.closureRemarks
                      ? (
                        <Tooltip title={line.closureRemarks}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                            {line.closureRemarks}
                          </Typography>
                        </Tooltip>
                      )
                      : (line.remarks || '—')}
                  </TableCell>
                  {!isVersionView && (
                    <TableCell>
                      {canClosePoLine(po, line, user?.role) ? (
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => openCloseLineDialog(line)}
                        >
                          Close line
                        </Button>
                      ) : '—'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!isVersionView && (po.submittedAt || po.storeApprovedAt || po.amApprovedAt || po.rejectedAt || (po.approvalRemarks && po.approvalRemarks.length)) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Approval history</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {po.submittedAt && (
              <li>Submitted {new Date(po.submittedAt).toLocaleString()} by {po.submittedBy?.firstName || '—'}</li>
            )}
            {po.storeApprovedAt && (
              <li>Store approved {new Date(po.storeApprovedAt).toLocaleString()} by {po.storeApprovedBy?.firstName || '—'}</li>
            )}
            {po.rejectedAt && (
              <li>Rejected {new Date(po.rejectedAt).toLocaleString()} by {po.rejectedBy?.firstName || '—'} {po.rejectionRemarks && `– ${po.rejectionRemarks}`}</li>
            )}
            {po.amApprovedAt && (
              <li>AM approved {new Date(po.amApprovedAt).toLocaleString()} by {po.amApprovedBy?.firstName || '—'}</li>
            )}
            {po.cancelledAt && (
              <li>Cancelled {new Date(po.cancelledAt).toLocaleString()} by {po.cancelledBy?.firstName || '—'} {po.cancellationRemarks && `– ${po.cancellationRemarks}`}</li>
            )}
            {(po.approvalRemarks || []).map((r, i) => (
              <li key={i}>
                {r.action === 'CORRECTION' ? 'Correction' : r.role}
                {r.role && r.action === 'CORRECTION' ? ` (${r.role})` : ''}: {r.remarks}
                {r.at ? ` (${new Date(r.at).toLocaleString()})` : ''}
              </li>
            ))}
          </Box>
        </Paper>
      )}

      {!isVersionView && (canManageStorePo || canApproveStore || canApproveAm) && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Actions</Typography>
          {po.status === 'PENDING_AM_APPROVAL' && canEditPo && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              You can edit line items and header details before issuing this purchase order.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {po.status === 'DRAFT' && canManageStorePo && (
              <Button variant="contained" size="small" onClick={() => runAction('submit')} disabled={actionLoading}>
                Submit
              </Button>
            )}
            {canApproveStore && (
              <>
                {canEditPo && onEdit && (
                  <Button variant="outlined" size="small" onClick={() => onEdit(poId)}>
                    Edit before approval
                  </Button>
                )}
                <Button variant="contained" color="success" size="small" onClick={() => runAction('approve-store')} disabled={actionLoading}>
                  Approve (Store)
                </Button>
                <Button variant="outlined" color="error" size="small" onClick={() => setRejectDialog({ open: true, remarks: '', action: 'reject-store' })}>
                  Reject
                </Button>
              </>
            )}
            {canApproveAm && (
              <>
                {canEditPo && onEdit && (
                  <Button variant="outlined" size="small" onClick={() => onEdit(poId)}>
                    Edit before approval
                  </Button>
                )}
                <Button variant="contained" color="success" size="small" onClick={() => runAction('approve-am')} disabled={actionLoading}>
                  Approve (AM)
                </Button>
                <Button variant="outlined" color="error" size="small" onClick={() => setRejectDialog({ open: true, remarks: '', action: 'reject-am' })}>
                  Reject
                </Button>
              </>
            )}
            {['DRAFT', 'PENDING_STORE_APPROVAL', 'PENDING_AM_APPROVAL', 'ISSUED'].includes(po.status) && canCancel && (
              <Button variant="outlined" color="error" size="small" onClick={() => setRejectDialog({ open: true, remarks: '', action: 'cancel' })}>
                Cancel PO
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Dialog
        open={closeLineDialog.open}
        onClose={() => setCloseLineDialog({ open: false, lineId: '', partNumber: '', closureRemarks: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Close line — {closeLineDialog.partNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The outstanding balance on this line will not be received. Use when the supplier has cancelled the part.
          </Typography>
          <TextField
            fullWidth
            label="Reason"
            value={PO_LINE_CLOSURE_REASON_LABELS.PART_CANCELLED}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            label="Closure remarks"
            multiline
            rows={3}
            value={closeLineDialog.closureRemarks}
            onChange={(e) => setCloseLineDialog((prev) => ({ ...prev, closureRemarks: e.target.value }))}
            helperText={`Required — minimum ${MIN_PO_LINE_CLOSURE_REMARKS_LENGTH} characters (supplier reference, date, etc.)`}
            error={String(closeLineDialog.closureRemarks || '').trim().length > 0
              && String(closeLineDialog.closureRemarks || '').trim().length < MIN_PO_LINE_CLOSURE_REMARKS_LENGTH}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseLineDialog({ open: false, lineId: '', partNumber: '', closureRemarks: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCloseLine}
            disabled={
              actionLoading
              || String(closeLineDialog.closureRemarks || '').trim().length < MIN_PO_LINE_CLOSURE_REMARKS_LENGTH
            }
          >
            Close line
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, remarks: '', action: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{rejectDialog.action === 'cancel' ? 'Cancel purchase order' : 'Reject'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={3}
            value={rejectDialog.remarks}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, remarks: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, remarks: '', action: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading}>
            {rejectDialog.action === 'cancel' ? 'Cancel PO' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={fieldDocsDialog.open}
        onClose={() => setFieldDocsDialog({ open: false, category: '', label: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{fieldDocsDialog.label ? `${fieldDocsDialog.label} Documents` : 'Field Documents'}</DialogTitle>
        <DialogContent dividers>
          {getDocsByCategory(fieldDocsDialog.category).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No documents uploaded for this field.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Uploaded At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getDocsByCategory(fieldDocsDialog.category).map((doc) => (
                  <TableRow key={doc._id}>
                    <TableCell>{doc.originalName || doc.filename}</TableCell>
                    <TableCell>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadShippingDoc(doc)}
                        disabled={downloadingDocId === doc._id}
                      >
                        <CloudDownload fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteShippingDoc(doc._id, fieldDocsDialog.category)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDocsDialog({ open: false, category: '', label: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      <PurchaseOrderDeleteDialog
        open={deleteDialogOpen}
        targets={po ? [{ _id: poId, poNumber: po.poNumber }] : []}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          setDeleteDialogOpen(false);
          if (onDeleted) onDeleted();
          else if (onBack) onBack();
        }}
      />

      <StoreDocumentsDialog
        open={docDialog}
        onClose={() => setDocDialog(false)}
        entityId={poId}
        entityType="purchaseorder"
        entityLabel={po.poNumber}
      />
    </Box>
  );
};

export default PurchaseOrderDetail;
